/* eslint-disable react/prop-types */
import React, {useRef} from 'react';
import {Platform} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {includes, keys} from 'lodash';

import BottomTabNavigation from '/component/layout/BottomTabNavigation';
import {Chat} from '/component/partial/Chat';
import InfoPopover from '/component/partial/InfoPopover';
import RefreshInfoPopover from '/component/partial/RefreshInfoPopover';
import ClientsScreen from '/component/screen/Clients';
import DashboardScreen from '/component/screen/Dashboard';
import PerformanceScreen from '/component/screen/Performance';
import ProfileScreen from '/component/screen/Profile';
import TeamsScreen from '/component/screen/Teams';
import {COACH} from '/enum/competency.enum';
import * as popoverEnums from '/enum/popover.enum';
import {NOTIFICATIONS, SEARCH} from '/enum/popover.enum';
import * as reportEnums from '/enum/records.enum';
import {
  MY_TEAMS,
  REPORT_ALL_REFERRALS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
  REPORT_NEW_COACHES_IN_ORGANIZATION,
  REPORT_NEW_FRONTLINE_COACHES,
} from '/enum/records.enum';
import {BUTTON_TYPES, EVENT_NAMES} from '/enum/tealium.enum';
import {useChatContext} from '/provider/ChatProvider';
import {createPopoverProvider} from '/provider/PopoverProvider';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withStatusBar} from '/provider/StatusBarProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {generateCallback} from '/util/component.util';
import {
  deepLinking,
  getDeepLinkingUrlFromStorage,
} from '/util/deep-linking/deep-linking.util';
import {isEmailAllowlisted} from '/util/emailAllowlist.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {withHOC} from '/util/hoc.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import MyTeamsReport from '../report/MyTeamsReport';

import {generateOnTabPress} from './Root.util';

import {RecordsSortContextProvider} from '/context/RecordsSort';

interface RootProps {
  tt: (key: string, fallback?: string) => string;
  statusBarSetDarkText: () => void;
  statusBarSetLightText: () => void;
  userId: number | null;
  competency: string;
  isPopoverOpen: boolean;
  lastPaidRank: string;
  highestRank: string;
  featureFlag: Record<string, any>;
  chatBetaUserEmails?: unknown;
  email?: string | null;
}

export class Root extends React.PureComponent<RootProps> {
  static defaultProps = {
    chatBetaUserEmails: [],
    email: null,
  };

  callOnDismiss: () => void;

  setOnDismiss?: (func: any) => void;

  onDismiss: () => void;

  popoverProvider: any;

  statusBarTextDark: boolean;

  constructor(...args: any[]) {
    // @ts-expect-error TS(2556): A spread argument must either have a tuple type or... Remove this comment to see the full error message
    super(...args);

    this.statusBarTextDark = false;

    this.onDismiss = () => {};
    this.setOnDismiss = (func: any) => {
      this.onDismiss = func;
    };
    this.callOnDismiss = () => this.onDismiss();

    this.popoverProvider = createPopoverProvider({
      onDismiss: this.callOnDismiss,
    });
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  componentDidMount() {
    const getUrl = async () => {
      const urlFromStorage = await getDeepLinkingUrlFromStorage();
      if (urlFromStorage) {
        await deepLinking.openUrl(urlFromStorage);
      }
    };
    getUrl();
  }

  setStatusBarDarkText() {
    const {statusBarSetDarkText} = this.props;
    if (statusBarSetDarkText) {
      statusBarSetDarkText();
    }
    this.statusBarTextDark = true;
  }

  setStatusBarLightText() {
    const {statusBarSetLightText} = this.props;
    if (statusBarSetLightText) {
      statusBarSetLightText();
    }
    this.statusBarTextDark = false;
  }

  getBottomTabsComponent() {
    const {tt, competency, lastPaidRank} = this.props;

    const Dashboard = this.getTabScreenComponent({
      Component: DashboardScreen,
      componentName: 'dashboard',
    });
    const Performance = this.getTabScreenComponent({
      Component: PerformanceScreen,
      componentName: 'performance',
    });
    const Teams = this.getTabScreenComponent({
      Component: TeamsScreen,
      componentName: 'teams',
    });
    const Watchlist = this.getTabScreenComponent({
      Component: MyTeamsReport,
      componentName: 'watchlist',
    });
    const Clients = this.getTabScreenComponent({
      Component: ClientsScreen,
      componentName: 'clients',
    });
    const Profile = this.getTabScreenComponent({
      Component: ProfileScreen,
      componentName: 'profile',
    });

    return () => {
      const navigationTabs = [
        {
          name: 'dashboard',
          activeIcon: 'dashboard-filled',
          icon: 'dashboard',
          label: tt('tab.label.dashboard', 'Dashboard'),
          component: Dashboard,
          onPress: generateOnTabPress({
            tabName: 'dashboard',
            buttonName: 'Dashboard',
            lastPaidRank: lastPaidRank,
          }),
        },
        {
          name: 'performance',
          activeIcon: 'performance-filled',
          icon: 'performance',
          label: tt('tab.label.performance', 'Performance'),
          component: Performance,
          onPress: generateOnTabPress({
            tabName: 'performance',
            buttonName: 'Performance',
            lastPaidRank: lastPaidRank,
          }),
        },
      ];

      navigationTabs.push({
        name: 'clients',
        activeIcon: 'clients-filled',
        icon: 'clients',
        label: tt('tab.label.clients', 'Clients'),
        component: Clients,
        onPress: generateOnTabPress({
          tabName: 'clients',
          buttonName: 'Clients',
          lastPaidRank: lastPaidRank,
        }),
      });
      if (competency !== COACH) {
        navigationTabs.push({
          name: 'teams',
          activeIcon: 'team-filled',
          icon: 'team',
          label: tt('tab.label.teams', 'Teams'),
          component: Teams,
          onPress: generateOnTabPress({
            tabName: 'teams',
            buttonName: 'Teams',
            lastPaidRank: lastPaidRank,
          }),
        });
      }
      navigationTabs.push({
        name: 'watchlist',
        activeIcon: 'watchlist-filled',
        icon: 'watchlist',
        label: tt('tab.label.watchlist', 'Watchlist'),
        component: Watchlist,
        onPress: generateOnTabPress({
          tabName: 'watchlist',
          buttonName: 'Watchlist',
          lastPaidRank: lastPaidRank,
        }),
      });
      navigationTabs.push({
        name: 'profile',
        activeIcon: 'profile',
        icon: 'profile',
        label: tt('tab.label.profile', 'Profile'),
        component: Profile,
        onPress: generateOnTabPress({
          tabName: 'profile',
          buttonName: 'Profile',
          lastPaidRank: lastPaidRank,
        }),
      });

      // @ts-expect-error TS(2322): Type '{ tabs: { name: string; activeIcon: string; ... Remove this comment to see the full error message
      return <BottomTabNavigation tabs={navigationTabs} />;
    };
  }

  getTabScreenComponent({Component, componentName}: any) {
    const {userId} = this.props;
    // @ts-expect-error TS(2339): Property 'rankName' does not exist on type 'Readonly... Remove this comment to see the full error message
    const {rankName} = this.props;
    const {highestRank} = this.props;
    const {lastPaidRank} = this.props;
    const {chatBetaUserEmails, email, featureFlag} = this.props;
    const {ff_chat = ''} = featureFlag;
    const showChatFeature =
      shouldReleaseFeature(ff_chat) &&
      isEmailAllowlisted({
        email,
        allowedEmails: chatBetaUserEmails,
      });
    const {Consumer: PopoverConsumer} = this.popoverProvider;

    const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.onPageLoad,
    });

    const sendInfoClickEvent =
      getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.informationClick,
        lastPaidRank: lastPaidRank,
      });

    return ({navigation: {navigate}, route}: any) => {
      const {showChat} = useChatContext();

      useFocusEffect(() => {
        // At this point in time all four screens has dark status bar. However if that is ever not the case,
        // update the related logic here to change the status bar style based on which screen
        // is active.
        this.setStatusBarDarkText();
      });

      const handleShowReportRef = useRef(
        ({
          recordsName,
          loadingData,
          reportHeading,
          screenName,
          filterName,
        }: any) => {
          if (includes(keys(reportEnums), recordsName)) {
            if (recordsName !== 'REPORT_CLIENT_TO_CONTACT') {
              const coachHighestRank = highestRank || 'na';
              const coachCurrentRank = rankName || 'na';

              // Special handling for REPORT_ALL_REFERRALS to send combined payload
              if (recordsName === REPORT_ALL_REFERRALS) {
                sendEvent({
                  buttonName: 'Referrals',
                  buttonType: BUTTON_TYPES.button,
                  currentScreenName: REPORT_ALL_REFERRALS,
                  coach_id: String(userId),
                  coach_highest_rank: coachHighestRank,
                  coach_current_rank: coachCurrentRank,
                  lastPaidRank: lastPaidRank,
                  source: 'REPORT_ALL_REFERRAL_NAVIGATION',
                });
              } else {
                const eligibleRecords = [
                  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
                  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
                  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
                  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
                  MY_TEAMS,
                  REPORT_NEW_FRONTLINE_COACHES,
                  REPORT_NEW_COACHES_IN_ORGANIZATION,
                ];
                const currentScreenName = eligibleRecords.includes(recordsName)
                  ? screenName
                  : recordsName;
                sendEvent({
                  currentScreenName: currentScreenName,
                  previousScreenName: componentName,
                  coach_highest_rank: coachHighestRank,
                  coach_current_rank: coachCurrentRank,
                  coach_id: String(userId),
                  lastPaidRank: lastPaidRank,
                  buttonType: loadingData?.buttonType || 'na',
                });
              }
            }
            navigate('card', {
              modalName: recordsName,
              modalProps: {
                loadingData,
                reportHeading,
                previousScreenName: componentName,
                filterName,
              },
            });
          }
        },
      );

      const handleNavigationIconPressRef = useRef((iconName: any) => {
        if (iconName === 'message' && showChatFeature) {
          showChat();
        } else if (iconName === 'notifications') {
          navigate('card', {modalName: NOTIFICATIONS, modalProps: {}});
        } else if (iconName === 'search') {
          navigate('card', {modalName: SEARCH, modalProps: {}});
        }
      });

      const screenComponent = useRef(({popoverShow}: any) => {
        const handleShowInfoPopoverRef = useRef((payload: any) => {
          popoverShow(InfoPopover, {
            ...payload,
            useModal: Platform.OS === 'android',
          });
        });

        const handleShowRefreshInfoPopoverRef = useRef((payload: any) => {
          sendInfoClickEvent({
            buttonName: 'Refresh Info Popover',
            screenName: route.name,
            lastPaidRank: lastPaidRank,
          });
          return popoverShow(RefreshInfoPopover, {
            ...payload,
            useModal: Platform.OS === 'android',
          });
        });

        const handleShowMyProfileRef = useRef(
          generateCallback({func: navigate, params: 'profile'}),
        );

        const handleOpenModalRef = useRef(
          ({modalName, modalProps = {}}: any = {}) => {
            if (includes(keys(popoverEnums), modalName)) {
              navigate('card', {modalName, modalProps});
            }
          },
        );

        return (
          <Component
            accessibilityElementsHidden={this.props.isPopoverOpen}
            onDismiss={this.setOnDismiss}
            route={route}
            popoverShow={popoverShow}
            onNavigate={navigate}
            onShowReport={handleShowReportRef.current}
            onNavigationIconPress={handleNavigationIconPressRef.current}
            onShowInfoPopover={handleShowInfoPopoverRef.current}
            onShowRefreshInfoPopover={handleShowRefreshInfoPopoverRef.current}
            onShowMyProfile={handleShowMyProfileRef.current}
            onOpenModal={handleOpenModalRef.current}
            showChatFeature={showChatFeature}
            userId={userId}
          />
        );
      });

      return (
        <PopoverConsumer>
          {({popoverShow}: any) => {
            const ScreenComponent = screenComponent.current;
            return <ScreenComponent popoverShow={popoverShow} />;
          }}
        </PopoverConsumer>
      );
    };
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  render() {
    const {Provider: PopoverProvider} = this.popoverProvider;
    const MainContent = this.getBottomTabsComponent();
    return (
      <RecordsSortContextProvider>
        <PopoverProvider>
          <MainContent />
          <Chat />
        </PopoverProvider>
      </RecordsSortContextProvider>
    );
  }
}

export default withHOC(
  withStatusBar,
  withSafeArea,
  withTranslation('screen.root'),
)(Root);
